import { ChangeEvent } from "react";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";
import {
  CreditDebitNoteOriginalBill,
  CreditDebitNoteOriginalLine,
  NoteReasonType,
  NoteType,
} from "../../types/credit-debit-note.type";
import { formatNumber } from "../../utils";
import { roundHalfUp } from "../../utils/money";
import {
  BillLineDraft,
  billLineError,
  billLineInput,
  billLineMaxQty,
  isUnitPriceKeyed,
  newBillLine,
  sanitizeInteger,
  sanitizeMoney,
} from "./noteDraft";
import { noteLineTotal } from "./totals";

interface BillLinesPickerProps {
  bill: CreditDebitNoteOriginalBill;
  billLines: BillLineDraft[];
  noteType: NoteType;
  /** The reason the note carries ("" while a credit note's is not picked yet). */
  reasonType: NoteReasonType | "";
  /** Mark the lines that break a cap — once a save has been tried. */
  showLineErrors: boolean;
  error?: string;
  onChange: (billLines: BillLineDraft[]) => void;
}

const MONEY_DECIMALS = 2;
/** The server prices a bill line's net unit price at 4 dp. */
const NET_PRICE_DECIMALS = 4;

const tableHeadSx = { bgcolor: (theme: Theme) => theme.palette.background.default };
const productCellSx = { minWidth: 200 };
const figureInputSx = { width: 96 };
const figureInputProps = {
  htmlInput: { inputMode: "decimal", style: { textAlign: "right" } },
} as const;

/** The net price at the satang when that is exact, else at the 4 dp the server prices with. */
const formatNetPrice = (value: number) =>
  formatNumber(value, {
    decimals: roundHalfUp(value, MONEY_DECIMALS) === value ? MONEY_DECIMALS : NET_PRICE_DECIMALS,
  });

const formatQty = (value: number) => formatNumber(value, { decimals: 0 });

/** Ticked lines stay in bill order, so the note's lines follow the bill's. */
const bySequence = (first: BillLineDraft, second: BillLineDraft) => first.sequence - second.sequence;

/** The keyed unit price is what a re-pricing note moves each piece by. */
const keyedPriceLabel = (noteType: NoteType) =>
  noteType === "CREDIT" ? "ลดราคา/หน่วย" : "เพิ่มราคา/หน่วย";

interface BillLineRowProps {
  billLine: CreditDebitNoteOriginalLine;
  /** The ticked line; undefined while the bill line is not on the note. */
  line: BillLineDraft | undefined;
  noteType: NoteType;
  reasonType: NoteReasonType | "";
  showLineErrors: boolean;
  onToggle: (sequence: number, isTicked: boolean) => void;
  onUpdate: (line: BillLineDraft) => void;
}

function BillLineRow({
  billLine,
  line,
  noteType,
  reasonType,
  showLineErrors,
  onToggle,
  onUpdate,
}: Readonly<BillLineRowProps>) {
  const isPriceKeyed = isUnitPriceKeyed(noteType, reasonType);
  const isExhausted = billLineMaxQty(billLine, noteType, reasonType) <= 0;
  const lineTotal = line ? noteLineTotal(billLineInput(line, billLine, isPriceKeyed)) : null;
  const lineError =
    showLineErrors && line ? billLineError(line, billLine, noteType, reasonType) : undefined;

  const handleToggle = (event: ChangeEvent<HTMLInputElement>) =>
    onToggle(billLine.original_item_sequence, event.target.checked);
  const handleQtyChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (line) onUpdate({ ...line, qty: sanitizeInteger(event.target.value) });
  };
  const handleUnitPriceChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (line) onUpdate({ ...line, unitPrice: sanitizeMoney(event.target.value) });
  };

  return (
    <TableRow selected={Boolean(line)}>
      <TableCell padding="checkbox">
        <Checkbox
          size="small"
          checked={Boolean(line)}
          disabled={!line && isExhausted}
          onChange={handleToggle}
          slotProps={{ input: { "aria-label": `เลือก ${billLine.product_code}` } }}
        />
      </TableCell>
      <TableCell sx={productCellSx}>
        <Typography variant="body2">{billLine.product_name ?? billLine.product_code}</Typography>
        <Typography variant="caption" color="text.secondary">
          {billLine.product_code}
        </Typography>
        {lineError && (
          <Typography variant="caption" color="error" display="block">
            {lineError}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">{formatQty(billLine.sold_qty)}</TableCell>
      <TableCell align="right">{formatQty(billLine.returned_qty)}</TableCell>
      <TableCell align="right">{formatQty(billLine.returnable_qty)}</TableCell>
      <TableCell align="right">{formatNetPrice(billLine.net_unit_price)}</TableCell>
      <TableCell align="right">
        <TextField
          size="small"
          value={line?.qty ?? ""}
          placeholder="0"
          disabled={!line}
          error={Boolean(lineError)}
          onChange={handleQtyChange}
          sx={figureInputSx}
          slotProps={figureInputProps}
        />
      </TableCell>
      {isPriceKeyed && (
        <TableCell align="right">
          <TextField
            size="small"
            value={line?.unitPrice ?? ""}
            placeholder="0.00"
            disabled={!line}
            error={Boolean(lineError)}
            onChange={handleUnitPriceChange}
            sx={figureInputSx}
            slotProps={figureInputProps}
          />
        </TableCell>
      )}
      <TableCell align="right">{formatNumber(lineTotal)}</TableCell>
    </TableRow>
  );
}

/**
 * The linked bill's lines: tick the ones the note corrects and key how many. A RETURN / DAMAGED
 * credit takes pieces back at the price the shop paid (the bill's net unit price), up to what is
 * still returnable; a re-pricing note keys the price change per piece, up to what was sold (and,
 * on a credit note, at most the net price). The server checks the same caps again on save.
 */
export default function BillLinesPicker({
  bill,
  billLines,
  noteType,
  reasonType,
  showLineErrors,
  error,
  onChange,
}: Readonly<BillLinesPickerProps>) {
  const isPriceKeyed = isUnitPriceKeyed(noteType, reasonType);
  const hasNoLines = bill.lines.length === 0;
  const lineBySequence = new Map(billLines.map((line) => [line.sequence, line]));

  const handleToggle = (sequence: number, isTicked: boolean) =>
    onChange(
      isTicked
        ? [...billLines, newBillLine(sequence)].sort(bySequence)
        : billLines.filter((line) => line.sequence !== sequence),
    );
  const handleUpdate = (updated: BillLineDraft) =>
    onChange(billLines.map((line) => (line.sequence === updated.sequence ? updated : line)));

  return (
    <Stack gap={1}>
      <Typography variant="body2" color="text.secondary">
        ยอดที่บิลนี้ยังลดหนี้ได้ (ก่อน VAT):{" "}
        <Typography component="span" variant="body2" fontWeight={700} color="text.primary">
          {formatNumber(bill.remaining_creditable_value)} บาท
        </Typography>
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {hasNoLines ? (
        <Typography variant="body2" color="text.secondary">
          บิลนี้ไม่มีรายการสินค้าที่ออกเอกสารอ้างอิงได้
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead sx={tableHeadSx}>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>สินค้า</TableCell>
                <TableCell align="right">ขาย</TableCell>
                <TableCell align="right">คืน/ลดหนี้แล้ว</TableCell>
                <TableCell align="right">คืนได้</TableCell>
                <TableCell align="right">ราคาสุทธิ/หน่วย</TableCell>
                <TableCell align="right">จำนวน</TableCell>
                {isPriceKeyed && <TableCell align="right">{keyedPriceLabel(noteType)}</TableCell>}
                <TableCell align="right">จำนวนเงิน</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bill.lines.map((billLine) => (
                <BillLineRow
                  key={billLine.original_item_sequence}
                  billLine={billLine}
                  line={lineBySequence.get(billLine.original_item_sequence)}
                  noteType={noteType}
                  reasonType={reasonType}
                  showLineErrors={showLineErrors}
                  onToggle={handleToggle}
                  onUpdate={handleUpdate}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
